import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  TextField,
  Chip,
} from '@mui/material';
import {
  Clock,
  Video,
  RotateCcw,
  Maximize2,
  X,
  Image as ImageIcon,
  Check,
  AlertCircle,
  Circle,
} from 'lucide-react';
import { convertSecondsToTimeTaken } from '../../../../../../utils/helpers/time';
import {
  AssessmentQuestionInfo,
  UserAssessmentResponse,
} from '../../../../../../utils/types/assessment_types';
import { IS_IOS } from '../../assigned_assessments';

type QuestionAreaProps = {
  questionType: string;
  setCurrentAnswer: (value: string | File | undefined) => void;
  recordedVideo: string | undefined;
  setRecordedVideo: (value: string | undefined) => void;
  currentQuestion: AssessmentQuestionInfo;
  timeRemaining: number | undefined;
  submitAnswer: (
    sampleBlob?: Blob[],
    sampleAudioBlob?: Blob[],
    skip?: boolean,
  ) => void;
  submissionDisabled: boolean;
  loading: boolean;
  uploadStatus: 'initial' | 'loading' | 'success' | 'error';
  setUploadStatus: (
    value:
      | ((
          prevState: 'initial' | 'loading' | 'success' | 'error',
        ) => 'initial' | 'loading' | 'success' | 'error')
      | 'initial'
      | 'loading'
      | 'success'
      | 'error',
  ) => void;
  handleQuestionSubmission: (
    video: File,
    timeExpired: boolean,
  ) => Promise<void>;
  responseUserAssessment: UserAssessmentResponse | undefined;
  handleNavigationResult: (navResponse: any) => void;
  questionCount: number;
  setQuestionCount: (value: ((prevState: number) => number) | number) => void;
  submitNoFile: (timeExpired: boolean) => Promise<void>;
  setRecordedAudio: (
    value:
      | ((prevState: string | undefined) => string | undefined)
      | string
      | undefined,
  ) => void;
  totalQuestions: number;
  isBehavioral: boolean;
  currentAnswer: string | File | undefined;
};

export const QuestionArea: React.FC<QuestionAreaProps> = ({
  questionType,
  setCurrentAnswer,
  recordedVideo,
  setRecordedVideo,
  currentQuestion,
  timeRemaining,
  submitAnswer,
  submissionDisabled,
  uploadStatus,
  submitNoFile,
  setRecordedAudio,
  totalQuestions,
  isBehavioral,
  questionCount,
}) => {
  const liveVideoElement = useRef<HTMLVideoElement>(null);
  const playbackVideoElement = useRef<HTMLVideoElement>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>();
  const [videoChunks, setVideoChunks] = useState<Blob[]>([]);
  const [recording, setRecording] = useState<boolean>(false);
  const [textAnswer, setTextAnswer] = useState<string>('');
  const hasHandledZero = useRef(false);
  const shouldAcceptChunks = useRef(false);
  const videoChunksRef = useRef<Blob[]>([]);
  const [audioRecorder, setAudioRecorder] = useState<MediaRecorder>();
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const audioChunksRef = useRef<Blob[]>([]);

  const isTimeExpired = timeRemaining !== undefined && timeRemaining < 0;

  useEffect(() => {
    let localStream: MediaStream | null = null;

    const initializeCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        localStream = stream;

        setMediaRecorder(new MediaRecorder(stream));

        const audioStream = new MediaStream(stream.getAudioTracks());
        setAudioRecorder(
          new MediaRecorder(audioStream, {
            mimeType: 'audio/webm',
          }),
        );

        if (liveVideoElement.current) {
          liveVideoElement.current.muted = true;
          liveVideoElement.current.srcObject = stream;
          liveVideoElement.current.autoplay = true;
        }
      } catch (err) {
        console.error('Failed to get media devices:', err);
      }
    };

    initializeCamera();

    return () => {
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
      if (liveVideoElement.current) {
        liveVideoElement.current.srcObject = null;
      }
    };
  }, [questionType, currentQuestion.id]);

  useEffect(() => {
    if (audioRecorder) {
      audioRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0 && shouldAcceptChunks.current) {
          audioChunksRef.current.push(event.data);
          setAudioChunks((prev) => [...prev, event.data]);
        }
      };
    }

    return () => {
      if (audioRecorder) {
        audioRecorder.ondataavailable = null;
      }
    };
  }, [audioRecorder]);

  // Reset local text state when question changes
  useEffect(() => {
    setTextAnswer('');
  }, [currentQuestion.id]);

  useEffect(() => {
    if (mediaRecorder) {
      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0 && shouldAcceptChunks.current) {
          videoChunksRef.current.push(event.data);
          setVideoChunks((prev) => [...prev, event.data]);
        }
      };
    }

    return () => {
      if (mediaRecorder) {
        mediaRecorder.ondataavailable = null;
      }
    };
  }, [mediaRecorder]);

  useEffect(() => {
    if (
      timeRemaining !== undefined &&
      timeRemaining === 0 &&
      videoChunks.length === 0
    ) {
      submitNoFile(true);
      return;
    }

    if (timeRemaining !== -1) {
      hasHandledZero.current = false;
      return;
    }

    if (hasHandledZero.current) return;
    hasHandledZero.current = true;

    if (recording) {
      stopRecording();
    } else if (submissionDisabled) {
      submitNoFile(true);
    }
  }, [questionType, timeRemaining, recording]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!recording) {
      startRecording();
    }
    if (isTimeExpired) return;
    const answer = event.target.value;
    setTextAnswer(answer);
    setCurrentAnswer(answer);
  };

  const startRecording = () => {
    let currentVideoRecorder = mediaRecorder;
    let currentAudioRecorder = audioRecorder;

    if (!currentVideoRecorder || !currentAudioRecorder) {
      if (liveVideoElement.current?.srcObject) {
        const stream = liveVideoElement.current.srcObject as MediaStream;
        currentVideoRecorder = new MediaRecorder(stream);
        const audioStream = new MediaStream(stream.getAudioTracks());
        currentAudioRecorder = new MediaRecorder(audioStream, {
          mimeType: 'audio/webm',
        });
        setMediaRecorder(currentVideoRecorder);
        setAudioRecorder(currentAudioRecorder);
      } else {
        console.error('No stream available to start recording');
        return;
      }
    }

    if (currentVideoRecorder && currentAudioRecorder) {
      videoChunksRef.current = [];
      setVideoChunks([]);
      audioChunksRef.current = [];
      setAudioChunks([]);
      shouldAcceptChunks.current = true;

      currentVideoRecorder.start(10);
      currentAudioRecorder.start(10);
      setRecording(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && audioRecorder) {
      shouldAcceptChunks.current = false;
      mediaRecorder.stop();
      audioRecorder.stop();
      setRecording(false);

      setTimeout(() => {
        storeRecording();
        setMediaRecorder(undefined);
        setAudioRecorder(undefined);
      }, 200);
    }
  };

  const storeRecording = () => {
    const videoBlob = new Blob(videoChunksRef.current, {
      type: 'video/webm; codecs=vp8',
    });
    const videoFile = new File([videoBlob], 'sample.webm', {
      type: 'video/webm',
    });

    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

    if (questionType !== 'Classic') {
      setCurrentAnswer(videoFile);
    }

    const videoUrl = URL.createObjectURL(videoBlob);
    setRecordedVideo(videoUrl);
    const audioURL = URL.createObjectURL(audioBlob);
    setRecordedAudio(audioURL);

    // Attach to playback element
    if (playbackVideoElement.current) {
      playbackVideoElement.current.src = videoUrl;
      playbackVideoElement.current.load();
      playbackVideoElement.current.autoplay = false;
    }
  };

  const redoRecording = async () => {
    setRecordedVideo(undefined);
    setCurrentAnswer(undefined);
    videoChunksRef.current = [];
    setVideoChunks([]);
    audioChunksRef.current = [];
    setAudioChunks([]);

    if (playbackVideoElement.current) {
      playbackVideoElement.current.src = '';
    }

    if (liveVideoElement.current?.srcObject) {
      const stream = liveVideoElement.current.srcObject as MediaStream;
      const newMediaRecorder = new MediaRecorder(stream);
      const audioStream = new MediaStream(stream.getAudioTracks());
      const newAudioRecorder = new MediaRecorder(audioStream, {
        mimeType: 'audio/webm',
      });

      setMediaRecorder(newMediaRecorder);
      setAudioRecorder(newAudioRecorder);

      shouldAcceptChunks.current = true;
      newMediaRecorder.start(10);
      newAudioRecorder.start(10);
      setRecording(true);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);

  const handleSubmitting = async (
    sampleBlob?: Blob[],
    sampleAudioBlob?: Blob[],
    skip?: boolean,
  ) => {
    setIsSubmitting(true);
    try {
      if (questionType === 'Classic' && recording) {
        stopRecording();
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      await submitAnswer(
        questionType === 'Classic' ? videoChunksRef.current : sampleBlob,
        questionType === 'Classic' ? audioChunksRef.current : sampleAudioBlob,
        skip,
      );

      videoChunksRef.current = [];
      setVideoChunks([]);
      audioChunksRef.current = [];
      setAudioChunks([]);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error('Submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRecordButton = () => {
    if (recordedVideo && timeRemaining) {
      return (
        <Button
          onClick={() => redoRecording()}
          variant="outlined"
          startIcon={<RotateCcw size={16} />}
          disabled={timeRemaining < 1 || uploadStatus === 'loading' || isSubmitting}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2.5,
            px: 2.5,
            py: 1,
          }}
        >
          Redo Recording
        </Button>
      );
    } else {
      return (
        <Button
          onClick={() => (recording ? stopRecording() : startRecording())}
          variant="contained"
          color={recording ? 'error' : 'primary'}
          disabled={(recording && videoChunks.length < 2) || isSubmitting}
          startIcon={
            recording ? (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: 'common.white',
                  borderRadius: 0.5,
                  animation: 'pulse 1s infinite',
                }}
              />
            ) : (
              <Circle size={14} fill="currentColor" />
            )
          }
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2.5,
            px: 3,
            py: 1,
            boxShadow: 2,
          }}
        >
          {recording ? 'Stop Recording' : 'Start Recording'}
        </Button>
      );
    }
  };

  const checkMediaType = () => {
    return !currentQuestion.fileUrl?.match(/\.(mp4|mkv|avi|webm)$/i);
  };

  const isImage = checkMediaType();

  /* ─── Full-screen media modal ─── */
  const renderMediaModal = () => {
    if (!currentQuestion.fileUrl) return null;
    return (
      <Dialog
        open={mediaModalOpen}
        onClose={() => setMediaModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'common.black',
            color: 'common.white',
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {isImage ? <ImageIcon size={20} color="#ffffff" /> : <Video size={20} color="#ffffff" />}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'common.white' }}>
              {isImage ? 'Image Reference' : 'Video Reference'}
            </Typography>
            <Chip
              label="Reference Material"
              size="small"
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.15)', color: 'common.white', fontSize: 10 }}
            />
          </Stack>
          <IconButton
            onClick={() => setMediaModalOpen(false)}
            size="small"
            sx={{ color: 'common.white', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'common.black',
          }}
        >
          {isImage ? (
            <Box
              component="img"
              src={currentQuestion.fileUrl}
              alt="Full-size preview"
              sx={{
                maxWidth: '100%',
                maxHeight: '75vh',
                objectFit: 'contain',
                borderRadius: 2,
              }}
            />
          ) : (
            <Box
              component="video"
              controls
              autoPlay
              playsInline
              src={currentQuestion.fileUrl}
              sx={{
                maxWidth: '100%',
                maxHeight: '75vh',
                borderRadius: 2,
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const renderQuestionMedia = () => {
    if (!currentQuestion.fileUrl) return null;
    return (
      <Stack spacing={1.5} sx={{ width: '100%' }}>
        {renderMediaModal()}
        {/* Label pill */}
        <Paper
          elevation={0}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.5,
            py: 0.75,
            borderRadius: 2.5,
            bgcolor: 'primary.50',
            border: '1px solid',
            borderColor: 'primary.100',
            width: 'fit-content',
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isImage ? <ImageIcon size={14} /> : <Video size={14} />}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', lineHeight: 1.1 }}>
              {isImage ? 'Image' : 'Video'} Reference
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.light', fontSize: 9, display: 'block', mt: 0.25 }}>
              Tap to expand full screen
            </Typography>
          </Box>
        </Paper>

        {/* Media card */}
        <Paper
          elevation={1}
          onClick={() => setMediaModalOpen(true)}
          sx={{
            position: 'relative',
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'common.black',
            cursor: 'pointer',
            maxHeight: 340,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {isImage ? (
            <Box
              component="img"
              src={currentQuestion.fileUrl}
              alt={`Question ${currentQuestion.id} media`}
              sx={{
                width: '100%',
                maxHeight: 340,
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <Box
              component="video"
              controls
              playsInline
              src={currentQuestion.fileUrl}
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: '100%',
                maxHeight: 340,
                objectFit: 'contain',
              }}
            />
          )}

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMediaModalOpen(true);
            }}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: 'common.white',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
            }}
          >
            <Maximize2 size={16} />
          </IconButton>
        </Paper>
      </Stack>
    );
  };

  /* ─── Unified Progress ─── */
  const renderProgress = () => (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 3,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        width: '100%',
      }}
    >
      <Stack spacing={2}>
        <LinearProgress
          variant="determinate"
          value={((totalQuestions - (questionCount - 1)) / totalQuestions) * 100}
          sx={{ height: 6, borderRadius: 3 }}
        />
        <Box sx={{ textAlign: 'center' }}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 0.5 }}>
            <Clock size={20} color="#2563eb" />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: -0.5 }}>
              {timeRemaining ? convertSecondsToTimeTaken(timeRemaining) : '00:00'}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
            Remaining Time
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  /* ─── Unified Sidebar ─── */
  const renderSidebar = () => (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        overflowY: 'auto',
        pr: 0.5,
        bgcolor: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(4px)',
        borderRadius: 3,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={2} sx={{ width: '100%' }}>
        {renderProgress()}
        {isBehavioral ? (
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              borderRadius: 3,
              border: '2px solid',
              borderColor: 'secondary.light',
              bgcolor: 'secondary.50',
              p: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
              Behavioural type questions may include 3 follow up questions
            </Typography>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              borderRadius: 3,
              border: '2px solid',
              borderColor: 'primary.light',
              bgcolor: 'primary.50',
              p: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
              Skill Questions
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main', mb: 0.5 }}>
              {questionCount <= 1 ? '0' : questionCount - 1}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Left to answer
            </Typography>
          </Paper>
        )}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: 'success.50', color: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Video size={14} />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Video Tips
          </Typography>
        </Stack>
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Stable internet required</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Quiet, well-lit environment</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>Re-recording allowed</Typography>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );

  const renderQuestionArea = () => {
    if (questionType === 'Classic') {
      return (
        <Stack spacing={2.5} sx={{ width: '100%' }}>
          {/* Progress bar and counter */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>{renderProgress()}</Box>
            {isBehavioral ? (
              <Paper
                elevation={0}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  minWidth: { md: 200 },
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: 'secondary.light',
                  bgcolor: 'secondary.50',
                  p: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Behavioural type questions may include 3 follow up questions
                </Typography>
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  minWidth: { md: 160 },
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: 'primary.light',
                  bgcolor: 'primary.50',
                  p: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                  Skill Questions
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main', mb: 0.5 }}>
                  {questionCount <= 1 ? '0' : questionCount - 1}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Left to answer
                </Typography>
              </Paper>
            )}
          </Stack>

          {/* Main content */}
          <Grid container spacing={2.5}>
            {currentQuestion.fileUrl && (
              <Grid item xs={12} md={6}>
                {renderQuestionMedia()}
              </Grid>
            )}

            <Grid item xs={12} md={currentQuestion.fileUrl ? 6 : 12}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                {/* Header bar */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Your Answer
                  </Typography>
                  {isTimeExpired && (
                    <Chip
                      icon={<AlertCircle size={14} />}
                      label="Time expired"
                      size="small"
                      color="error"
                      sx={{ fontSize: 11, fontWeight: 600 }}
                    />
                  )}
                </Stack>

                <Box sx={{ p: 2 }}>
                  <TextField
                    multiline
                    fullWidth
                    minRows={8}
                    value={textAnswer}
                    onChange={handleChange}
                    disabled={isTimeExpired}
                    placeholder="Type your response here..."
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: 14,
                        bgcolor: isTimeExpired ? 'action.hover' : 'background.paper',
                      },
                    }}
                  />
                </Box>

                {/* Footer with char count + submit */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {textAnswer.length} characters
                  </Typography>

                  <Button
                    onClick={() =>
                      handleSubmitting(
                        videoChunksRef.current,
                        audioChunksRef.current,
                        false,
                      )
                    }
                    variant="contained"
                    color="success"
                    disabled={submissionDisabled || uploadStatus === 'loading' || isSubmitting}
                    startIcon={showSuccess ? <Check size={16} /> : undefined}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2.5,
                      px: 3,
                      py: 1,
                    }}
                  >
                    {showSuccess ? 'Submitted!' : isSubmitting ? 'Submitting...' : 'Submit Response'}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      );
    }

    // Modern / Behavioral / Video templates
    return (
      <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
        {/* Mobile Progress */}
        <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
          {renderProgress()}
        </Grid>

        {/* Media column if present */}
        {currentQuestion.fileUrl && (
          <Grid item xs={12} md={5}>
            {renderQuestionMedia()}
          </Grid>
        )}

        {/* Video recorder column */}
        <Grid item xs={12} md={currentQuestion.fileUrl ? 7 : 8}>
          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                p: 0.75,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '16/9',
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  bgcolor: 'common.black',
                }}
              >
                <Box
                  component="video"
                  ref={liveVideoElement}
                  autoPlay
                  playsInline
                  muted
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: recordedVideo ? 0 : 1,
                    transition: 'opacity 500ms ease',
                  }}
                />
                <Box
                  component="video"
                  ref={playbackVideoElement}
                  controls={Boolean(recordedVideo)}
                  playsInline
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: recordedVideo ? 1 : 0,
                    pointerEvents: recordedVideo ? 'auto' : 'none',
                    transition: 'opacity 500ms ease',
                  }}
                />
                {recording && (
                  <Chip
                    icon={
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'error.main',
                          animation: 'pulse 1s infinite',
                        }}
                      />
                    }
                    label="LIVE RECORDING"
                    color="error"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      fontWeight: 700,
                      fontSize: 10,
                      backdropFilter: 'blur(4px)',
                      bgcolor: 'rgba(0,0,0,0.7)',
                    }}
                  />
                )}
              </Box>
            </Paper>

            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} flexWrap="wrap">
              {renderRecordButton()}
              {(recordedVideo || questionType === 'Classic') && (
                <Button
                  onClick={() =>
                    handleSubmitting(
                      videoChunksRef.current,
                      audioChunksRef.current,
                      false,
                    )
                  }
                  variant="contained"
                  color="success"
                  disabled={submissionDisabled || uploadStatus === 'loading' || isSubmitting}
                  startIcon={showSuccess ? <Check size={16} /> : undefined}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2.5,
                    px: 3,
                    py: 1,
                  }}
                >
                  {showSuccess ? 'Submitted!' : isSubmitting ? 'Submitting...' : 'Submit Response'}
                </Button>
              )}
            </Stack>

            {currentQuestion.fileUrl && (
              <Stack spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    {renderProgress()}
                  </Grid>
                  <Grid item xs={4}>
                    {isBehavioral ? (
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: '2px solid',
                          borderColor: 'secondary.light',
                          bgcolor: 'secondary.50',
                          p: 2,
                          textAlign: 'center',
                          height: '100%',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Behavioural type questions may include 3 follow up questions
                        </Typography>
                      </Paper>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: '2px solid',
                          borderColor: 'primary.light',
                          bgcolor: 'primary.50',
                          p: 2,
                          textAlign: 'center',
                          height: '100%',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                          Skill Questions
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main', mb: 0.5 }}>
                          {questionCount <= 1 ? '0' : questionCount - 1}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Left to answer
                        </Typography>
                      </Paper>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        height: '100%',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: 'success.50', color: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Video size={12} />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Video Tips
                        </Typography>
                      </Stack>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>• Stable internet required</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>• Quiet environment</Typography>
                        <Typography variant="caption" sx={{ color: 'success.main', fontSize: 10, fontWeight: 600 }}>• Re-recording allowed</Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Stack>
            )}
          </Stack>
        </Grid>

        {/* Sidebar if no media */}
        {!currentQuestion.fileUrl && (
          <Grid item xs={12} md={4} sx={{ height: '100%' }}>
            {renderSidebar()}
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box sx={{ width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {renderQuestionArea()}
    </Box>
  );
};

